import React from 'react';
import PropTypes from 'prop-types';

import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import {styled} from '@mui/system';

import {concatUrl} from '../helpers/url';
import {postCall} from '../helpers/request';
import GlobalContext from '../helpers/context';
import {checkUserRoles} from '../helpers/authentication';
import ActionButton from '../component/atoms/ActionButton';


const StyledFormControl = styled(FormControl)({
    width: 500
});

const StyledErrorMessage = styled(Typography)(({ theme }) =>({
    color: theme.custom.color.error,
    fontWeight: theme.custom.font.bold,
    marginTop: 5,
    marginLeft: 15
}));

const StyledDialogActions = styled(DialogActions)({
    marginBottom: 5
});


class CurationProfileLinkDialog extends React.Component {

    constructor(props) {
        super(props);

        this.state = {
            link_label: this.props.link_label || '',
            link_url: this.props.link_url || '',
            form_errors: {}
        };

        this.form_data = {};
        this.accepted_roles = ['admin', 'manager', 'reviewer'];
        this.save_curation_profile_metadata_url = concatUrl(window.ROOT_URL, 'api', 'curation_profile', 'metadata', 'save');
    }

    componentDidMount(){
        this.context.checkUserAccess(this.conditionCallback);
    }

    conditionCallback = (user) => {
        return checkUserRoles(user, this.accepted_roles);
    };

    componentDidUpdate(prevProps){
        if(this.props.link_label !== prevProps.link_label || this.props.link_url !== prevProps.link_url){
            this.setState({
                link_label: this.props.link_label,
                link_url: this.props.link_url
            });
        }
    }

    formValidated = () => {
        let form_errors = {
            'label': this.state.link_label.trim().length === 0 ? 'Please enter a label' : null,
            'url': this.state.link_url.trim().length === 0 ? 'Please enter an url' : null,
        };

        this.form_data = {
            workspace_id: this.props.workspace_id,
            curation_profile_id: this.props.curation_profile_id,
            link_label: this.state.link_label.trim(),
            link_url: this.state.link_url.trim(),
        };

        if(this.props.link_key){
            // edit existing link
            this.form_data['link_key'] = this.props.link_key;
        }

        this.setState({form_errors: form_errors});
        let form_is_valid = true;
        /*eslint-disable no-unused-vars */
        for (const key in form_errors){
            if (form_errors[key]){
                form_is_valid = false;
                break;
            }
        }
        /*eslint-enable no-unused-vars */

        return form_is_valid;
    };

    // +++ save curation profile link +++++++++++++++++++++++++++++++++++++++++++++++

    saveLinkFailed = () => {
        this.context.displayGlobalError('Backend service failure: Could not save the link.');
    };

    saveLinkDone = (data) => {
        this.context.displayGlobalInfo('The link was saved successfully');
        this.setState({
            form_errors: {},
            link_label: '',
            link_url: ''
        });
        this.props.on_save(data.metadata.links);
    };

    saveLink = () => {
        if (this.formValidated()){
            postCall(this.save_curation_profile_metadata_url , this.form_data,
                this.saveLinkDone,
                this.saveLinkFailed);
        }
    };

    showErrorMessage = (key) => {
        if (key in this.state.form_errors && this.state.form_errors[key]) {
            return (
                <StyledErrorMessage data-bt={`error_${key}`}>
                    {this.state.form_errors[key]}
                </StyledErrorMessage>
            );
        }
    };

    errorMode = (key) => {
        return key in this.state.form_errors && this.state.form_errors[key] !== null;
    };

    handleClose = () => {
        this.props.on_close();
        this.setState({
            link_label: '',
            link_url: '',
            form_errors: {}
        });
        this.form_data = {};
    };

    render() {
        const title = this.props.link_key ? 'Edit link' : 'Create link';
        return (
            <GlobalContext.Consumer>{() => (
                <Dialog open={this.props.dialog_is_open} aria-labelledby="form-dialog-title"
                    data-bt="link_dialog">
                    <DialogTitle id="form-dialog-title">{title}</DialogTitle>
                    <DialogContent>
                        <StyledFormControl fullWidth={true}>
                            <TextField
                                autoFocus
                                id="linkLabel"
                                label="Label"
                                slotProps={{ htmlInput: {'data-bt': 'link_label'} }}
                                required
                                error={this.errorMode('label')}
                                value={this.state.link_label}
                                onChange={(event) => {
                                    this.setState({link_label: event.target.value});
                                }}
                                margin="normal"
                                variant="outlined"
                                fullWidth={true}
                            />
                            {this.showErrorMessage('label')}
                            <TextField
                                id="linkUrl"
                                label="URL"
                                slotProps={{ htmlInput: {'data-bt': 'link_url'} }}
                                required
                                error={this.errorMode('url')}
                                value={this.state.link_url}
                                onChange={(event) => {
                                    this.setState({link_url: event.target.value});
                                }}
                                margin="normal"
                                variant="outlined"
                                fullWidth={true}
                            />
                            {this.showErrorMessage('url')}
                        </StyledFormControl>
                    </DialogContent>
                    <StyledDialogActions>
                        <ActionButton text="Cancel" dataBt="cancel_button" actionCallback={this.handleClose} linkStyle={true} hotKey="alt+x" />
                        <ActionButton text="Save" dataBt="save_button" actionCallback={this.saveLink} linkStyle={true} hotKey="alt+s" />
                    </StyledDialogActions>
                </Dialog>
            )}
            </GlobalContext.Consumer>
        );
    }
}

CurationProfileLinkDialog.contextType = GlobalContext;

CurationProfileLinkDialog.propTypes = {
    dialog_is_open: PropTypes.bool,
    on_close: PropTypes.func,
    on_save: PropTypes.func,
    curation_profile_id: PropTypes.number.isRequired,
    workspace_id: PropTypes.number.isRequired,
    link_key: PropTypes.string,
    link_label: PropTypes.string,
    link_url: PropTypes.string
};

export default CurationProfileLinkDialog;
